enum PermissionClassification {
  ViewMyProfile = 1,
  EditMyProfile = 2,
  SupplierOnboardingRequestsSent = 3,
  SupplierOnboardingRequestsReceived = 4,
  ManageUsers = 5,
  ManageUserRoles = 6,
  MyClients = 7,
  MySuppliers = 8,
  ConnectionRequests = 9,
  InvitedConnections = 10,
  MyPotentialClients = 11,
  CorporateOnboarding = 13,
  VirtualMarketPlace = 14,
  StartAConversation = 15,
  OnboardSupplier = 16,
  ConnectAsPotentialSupplier = 17,
  ConnectAsPotentialClient = 18,
  ViewPlatformCompanyData = 19,
  AccessPlatformConfiguration = 20,
  CanAccessWorkcentreAsAgent = 21,
  ExportSupplierPdf = 22,
  UpdateSupplierStatus = 23,
  /**
   * Review/approve/reject pending branch-onboarding requests (Epic #4183, #6070).
   * Gates the FE page, nav item and action buttons. Backend gates the same endpoints
   * via the "BranchRequestManagement" policy, which (per #6355) now requires this
   * permission id in the `extension_permissions` token claim — replacing the old
   * `company.branchrequests.manage` OAuth scope.
   */
  ManageBranchApprovals = 24,
  /** Access the manual onboarding initiator stepper and review layout. */
  ManualOnboarding = 25,
  /** Start a governed reset of a stuck supplier-approval reviewer claim (PBI #6428). */
  SupplierApprovalResetStarter = 26,
  /**
   * Review/approve/reject a supplier's proposed Bank Details change (Epic #4184, #6080).
   * Gates the "Bank Details" category of the Supplier Update Approvals workflow, which
   * requires a minimum of 2 approvals before the update is applied.
   */
  SupplierUpdateApproveBankDetails = 27,
  /**
   * Review/approve/reject a supplier's proposed Tax Clearance change (Epic #4184, #6080).
   * Gates the "Tax Clearance" category of the Supplier Update Approvals workflow.
   */
  SupplierUpdateApproveTaxClearance = 28,
  /**
   * Review/approve/reject a supplier's proposed Tax Information (Tax PIN/VAT) change
   * (Epic #4184, #6080). Gates the "Tax Information" category of the Supplier Update
   * Approvals workflow.
   */
  SupplierUpdateApproveTaxInformation = 29,
  /**
   * Review/approve/reject a supplier's proposed COIDA change (Epic #4184, #6080).
   * Gates the "COIDA" category of the Supplier Update Approvals workflow.
   */
  SupplierUpdateApproveCoida = 30,
  /**
   * Review/approve/reject a supplier's proposed BBEE change (Epic #4184, #6080).
   * Gates the "BBEE" category of the Supplier Update Approvals workflow.
   */
  SupplierUpdateApproveBbbee = 31,
  /**
   * Review/approve/reject a supplier's proposed Company Details change (Epic #4184, #6080).
   * Gates the "Company Details" category of the Supplier Update Approvals workflow.
   */
  SupplierUpdateApproveCompanyDetails = 32,
  /**
   * Review/approve/reject a supplier's proposed Contact Details change (Epic #4184, #6080).
   * Gates the "Contact Details" category of the Supplier Update Approvals workflow.
   */
  SupplierUpdateApproveContactDetails = 33,
  /**
   * Review/approve/reject a supplier's proposed Documents change (Epic #4184, #6080).
   * Gates the "Documents" category of the Supplier Update Approvals workflow.
   */
  SupplierUpdateApproveDocuments = 34,
  /**
   * Review/approve/reject a buyer-initiated Payment Terms change (Epic #4184, #6080).
   * Gates the "Payment Terms" category of the Supplier Update Approvals workflow.
   * Registered now, unassigned until buyer-initiated updates (US-E4 / #6415).
   */
  SupplierUpdateApprovePaymentTerms = 35,
  /**
   * Review/approve/reject a buyer-initiated Currency change (Epic #4184, #6080).
   * Gates the "Currency" category of the Supplier Update Approvals workflow.
   * Registered now, unassigned until buyer-initiated updates (US-E4 / #6415).
   */
  SupplierUpdateApproveCurrency = 36,
  /**
   * Review/approve/reject a buyer-initiated ERP Vendor Number change (Epic #4184, #6080).
   * Gates the "ERP Vendor Number" category of the Supplier Update Approvals workflow.
   * Registered now, unassigned until buyer-initiated updates (US-E4 / #6415).
   */
  SupplierUpdateApproveErpVendorNumber = 37,
  /**
   * Trigger a full delete-and-rebuild resync of the master-companies Elastic index
   * from Bbbee/Company service data (Epic #4184, US-6 #7134). Gates the Resync
   * button on the Platform Admin Master Data Sync tab; backend enforces the same
   * permission id on POST /api/admin/master-data-sync/resync.
   */
  MasterDataResync = 38,
  /**
   * COI ("Conflict of Interest") tab permissions (PBI #7749). These ids are a contract shared
   * with #7750 and #7751 — do NOT renumber or reuse them. They start at 39 (not the Technical
   * Document's 38-45) because 38 was already taken by MasterDataResync above.
   * Keep value-aligned with BE: B1Link.Utilities/Authentication/PermissionClassificiation.cs.
   */
  CoiScreenings = 39,
  CoiCases = 40,
  CoiDisclosures = 41,
  CoiRegistersPeople = 42,
  CoiRegistersApprovedSuppliers = 43,
  CoiRegistersGifts = 44,
  CoiCompanyNetwork = 45,
  CoiSettings = 46,
  /**
   * #8732: View Supplier B-BBEE Details. View only — opens the client B-BBEE Details tab under
   * Network and Connections (US-4). Contract shared with the backend enum, the Company DB seed and
   * the role picker; pinned by PermissionClassification.test.ts.
   */
  ViewSupplierBbbeeDetails = 47,
  /** Support mode theme and company switcher for support staff / platform admins */
  SupportCompanyContextSwitch = 10008,
  /** Manage Microsoft Entra tenant to company mappings for Sign in with Microsoft */
  ManageIdpTenantMappings = 10009
}

export default PermissionClassification;
